import { _ } from "~/i18n";
import { getImage } from "./utils";
import getWasmTexturesData, { TWasmTextureNames } from "./wasm-textures-data";
import { WASM_TEXTURES } from "./const";
import { getCircleImage, getTextureMetaData } from "./wasm-textures-utils";

interface ICustomTexture {
	imageData: ImageData;
	canvas: HTMLCanvasElement;
}

export default class WasmTextures {
	_image: HTMLImageElement;

	_canvas: HTMLCanvasElement;

	_ctx: CanvasRenderingContext2D;

	constructor() {
		this._image = null;
		this._canvas = document.createElement("canvas");
		this._ctx = this._canvas.getContext("2d");
	}

	async load() {
		const image = await getImage(WASM_TEXTURES.mainTexture);

		this._image = image;

		if (this._image) {
			this._canvas.width = this._image.naturalWidth;
			this._canvas.height = this._image.naturalHeight;
			this._ctx.drawImage(this._image, 0, 0);
		} else {
			this._canvas.width = WASM_TEXTURES.defSize;
			this._canvas.height = WASM_TEXTURES.defSize;
			this._ctx.fillStyle = "red";
			this._ctx.fillRect(0, 0, WASM_TEXTURES.defSize, WASM_TEXTURES.defSize);
		}
	}

	getWasmTexture(name: TWasmTextureNames) {
		const metaData = getTextureMetaData(name);
		// souradnice z textur
		const imageData = metaData.data
			? this._ctx.getImageData(metaData.data.x, metaData.data.y, metaData.data.width, metaData.data.height)
			: this._ctx.getImageData(0, 0, WASM_TEXTURES.defSize, WASM_TEXTURES.defSize);

		return {
			width: metaData.width,
			height: metaData.height,
			anchorX: metaData.anchorX,
			anchorY: metaData.anchorY,
			imageData,
		};
	}

	/**
	 * Ziskani markeru pro vlastni body
	 */
	getSmallNumber({
		value,
		isActive = false,
		opacity = 1,
	}: {
		value: number;
		isActive?: boolean;
		opacity?: number;
	}): ICustomTexture {
		const data = getWasmTexturesData(isActive ? "small-active" : "small-deactive");
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		// vykreslime marker
		ctx.globalAlpha = opacity;
		// nemuzu put image data, protoze tam pak nejde nastavit opacity
		ctx.drawImage(this._image, data.x, data.y, data.width, data.height, 0, 0, data.width, data.height);
		// text - 13px 1x, 26px 2x
		ctx.font = "26px Arial";
		ctx.fillStyle = "black";

		const drawText = value.toString();
		let offsetLeft = 0;
		const offsetTop = 40;

		if (drawText.length === 1) {
			offsetLeft = 24;
		} else if (drawText.length === 2) {
			offsetLeft = 17;
		} else if (drawText.length === 3) {
			offsetLeft = 10;
		}

		ctx.fillText(drawText, offsetLeft, offsetTop);

		return {
			imageData: ctx.getImageData(0, 0, data.width, data.height),
			canvas: newCanvas,
		};
	}

	/**
	 * Ziskani textove ikonky pro routu start/cil
	 */
	getGetRouteStartEnd({
		isEnd = false,
		isActive = false,
		opacity = 1,
	}: {
	isEnd?: boolean;
	isActive?: boolean;
	opacity?: number;
	}): ICustomTexture {
		const textureName = isActive ? "big-active" : "big-deactive";
		const metaData = getTextureMetaData(textureName);
		const data = getWasmTexturesData(textureName);
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		ctx.globalAlpha = opacity;
		// vykreslime marker
		// nemuzu put image data, protoze tam pak nejde nastavit opacity
		ctx.drawImage(this._image, data.x, data.y, data.width, data.height, 0, 0, data.width, data.height);
		// text - 13px 1x, 26px 2x
		ctx.font = "22px Arial";
		ctx.fillStyle = "black";

		const value = isEnd
			? _("route.item.end")
			: _("route.item.start");
		const drawText = value.toLocaleUpperCase();
		const measure = ctx.measureText(drawText);
		// musime znovu vynasobit 2x - je to retina
		const offsetLeft = (metaData.anchorX * WASM_TEXTURES.ratio) - (measure.width / 2);
		const offsetTop = 55;

		ctx.fillText(drawText, offsetLeft, offsetTop);

		return {
			imageData: ctx.getImageData(0, 0, data.width, data.height),
			canvas: newCanvas,
		};
	}

	getLocationShareDeviceNumber(order: number, isActive: boolean): ICustomTexture {
		const newCanvas = document.createElement("canvas");
		const data = getWasmTexturesData(isActive ? "position-share-device-active" : "position-share-device-deactive");
		const ctx = newCanvas.getContext("2d");
		const drawText = order.toString();

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		// vykreslime marker
		ctx.putImageData(this._ctx.getImageData(data.x, data.y, data.width, data.height), 0, 0);
		// text
		ctx.font = `bold ${14 * WASM_TEXTURES.ratio}px Arial`;
		ctx.fillStyle = "#5f7182";

		const metrics = ctx.measureText(drawText);
		const fontWidth = metrics.width;
		const fontHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

		ctx.fillText(drawText, (data.width / 2) - (fontWidth / 2), data.height - (2 * fontHeight) - (1 * WASM_TEXTURES.ratio));

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}

	async getLocationShareImage(url: string, isActive: boolean, isGray: boolean): Promise<ICustomTexture> {
		const imageData = await getCircleImage(url, 40 * WASM_TEXTURES.ratio, isGray);
		const imageCanvas = imageData.canvas;
		const newCanvas = document.createElement("canvas");
		const data = getWasmTexturesData(isActive ? "position-share-image-active" : "position-share-image-deactive");
		const ctx = newCanvas.getContext("2d");
		const pos = (data.width - imageCanvas.width) / 2;

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		// kolecko
		ctx.putImageData(this._ctx.getImageData(data.x, data.y, data.width, data.height), 0, 0);
		// fotka
		ctx.drawImage(imageCanvas, 0, 0, imageCanvas.width, imageCanvas.height, pos, pos - 2, imageCanvas.width, imageCanvas.height);

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}
}
